import React from 'react'
import { Link } from 'react-router-dom'
import {Sparkles, Zap, ChevronRight, Home} from 'lucide-react'

export function CategoryModern() {
  const modernShows = [
    {
      id: 1,
      title: 'Tom & Jerry Magic Ring',
      description: 'The hilarious chaos of Tom and Jerry Magic with modern animation. The Tom and Jerry Ring chase is the ultimate cat and mouse fantasy.',
      image: 'https://static.lumi.new/67/678dc25a595a0cdc8d11f06c2bbd13e0.png',
      link: '/review/tom-jerry-magic-ring',
      year: '2002',
      keywords: 'tom & jerry magic ring, tom and jerry magic, modern classic'
    },
    {
      id: 2,
      title: 'Avatar: The Last Airbender',
      description: 'Master the elements in this epic tale of magic, friendship, and destiny. A modern masterpiece of animated storytelling.',
      image: 'https://images.pexels.com/photos/8923177/pexels-photo-8923177.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: '2005-2008',
      keywords: 'avatar, elemental magic, modern epic'
    },
    {
      id: 3,
      title: 'Steven Universe',
      description: 'Join Steven and the Crystal Gems in their magical adventures protecting Earth with the power of gemstones.',
      image: 'https://images.pexels.com/photos/8923212/pexels-photo-8923212.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: '2013-2019',
      keywords: 'crystal gems, modern cartoon, magical powers'
    },
    {
      id: 4,
      title: 'The Owl House',
      description: 'A human girl discovers a portal to a magical realm. Modern Disney magic with unique character development.',
      image: 'https://images.pexels.com/photos/6985045/pexels-photo-6985045.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: '2020-2023',
      keywords: 'magic school, modern disney, witch academy'
    },
    {
      id: 5,
      title: 'Kipo and the Age of Wonderbeasts',
      description: 'A post-apocalyptic world filled with mutant magic and music. Fresh take on magical transformation.',
      image: 'https://images.pexels.com/photos/8613095/pexels-photo-8613095.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: '2020',
      keywords: 'modern netflix, magical creatures, transformation'
    },
    {
      id: 6,
      title: 'Hilda',
      description: 'Explore a world of magical creatures, trolls, and Scandinavian folklore in this beautifully animated series.',
      image: 'https://images.pexels.com/photos/7991226/pexels-photo-7991226.jpeg?auto=compress&cs=tinysrgb&w=800',
      year: '2018-Present',
      keywords: 'nordic magic, modern animation, folklore'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-sm shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-pink-500" />
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pink-500 via-orange-500 to-teal-500 bg-clip-text text-transparent">
                MagicCartoon.com
              </h1>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-600 hover:text-pink-500 font-semibold transition-colors"
            >
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline">Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-4">
            <Zap className="w-16 h-16 text-white animate-bounce" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Modern Magic Cartoons
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Contemporary animated series pushing boundaries with innovative storytelling and stunning magical worlds.
          </p>
        </div>
      </section>

      {/* Shows Grid */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {modernShows.map((show) => (
              <div 
                key={show.id}
                className="bg-white rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-all duration-300 border-2 border-cyan-200"
              >
                <div className="relative">
                  <img 
                    src={show.image}
                    alt={show.title}
                    className="w-full h-64 object-cover"
                  />
                  <span className="absolute top-4 right-4 bg-cyan-500 text-white px-4 py-2 rounded-full font-bold text-sm">
                    {show.year}
                  </span>
                </div>
                <div className="p-6 space-y-4">
                  <h3 className="text-2xl font-bold text-gray-800">{show.title}</h3>
                  <p className="text-gray-600">{show.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {show.keywords.split(', ').map((keyword, idx) => (
                      <span 
                        key={idx}
                        className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm font-semibold"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                  {show.link ? (
                    <Link 
                      to={show.link}
                      className="group bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 w-fit"
                    >
                      Read More
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ) : (
                    <button className="group bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2">
                      Coming Soon
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
